import React, { MouseEvent, useCallback } from 'react';
import classNames from 'classnames';
import { useAnalytics } from '@johnlewispartnership/wtr-website-analytics';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/TextLink';
import { ChevronRight } from '@johnlewispartnership/wtr-ingredients/dist/foundations/icons';
import { usePathname } from 'hooks/use-pathname';
import { usePodObserver } from 'hooks/use-pod-observer';
import { urls } from 'constants/urls';
import config from 'config';

import styles from './ShopByList.module.scss';

export interface ShopByListProps {
  hidden?: boolean;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  searchbox?: boolean;
}

const SHOP_BY_LIST_CTA_TEXT = 'Shop by list';

let lastTrackedPathname: string | null = null;

export const ShopByList = ({ hidden = false, onClick, searchbox }: ShopByListProps) => {
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();

  const onImpression = useCallback(() => {
    if (hidden || lastTrackedPathname === pathname) {
      return;
    }

    lastTrackedPathname = pathname;

    trackEvent({
      event: `feature_cta_impression`,
      feature: 'list_to_basket',
      feature_cta_id: 'search_box_list_to_basket',
      link_text: SHOP_BY_LIST_CTA_TEXT,
      link_url: urls.shopByList,
      page_location: config.clientHost + pathname,
    });
  }, [hidden, pathname, trackEvent]);

  const ctaRef = usePodObserver(onImpression);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      trackEvent({
        event: `feature_cta_click`,
        feature: 'list_to_basket',
        feature_cta_id: 'search_box_list_to_basket',
        link_text: SHOP_BY_LIST_CTA_TEXT,
        link_url: urls.shopByList,
        page_location: config.clientHost + pathname,
      });

      onClick?.(event);
    },
    [onClick, pathname, trackEvent],
  );

  if (hidden) {
    return null;
  }

  return (
    <>
      <TextLink
        className={classNames(styles.cta, {
          [styles.searchboxCta]: searchbox,
        })}
        component="a"
        href={urls.shopByList}
        onClick={handleClick}
        ref={ctaRef}
      >
        {SHOP_BY_LIST_CTA_TEXT}
      </TextLink>
      {!searchbox && <ChevronRight size="xsmall" className={styles.chevronRightIcon} />}
    </>
  );
};
